import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import DivStyle from "../../components/style/div.component";
import Table from "../../components/table/Table";
import Page from "../../components/table/paging";
import { FormProps } from "../../components/form/interface/formInterface";
import ButtonStyle from "../../components/style/button.component";
import InputFormStyle from "../../components/style/input_form.component";
import Icon from "@mdi/react";
import { mdiMagnify } from "@mdi/js";
import SmallForm from "../../components/form/small_form";
import PosterManager from "./posterManager";
import {
  IGalleryImagesResponse,
  IGalleryResponse,
} from "./interface/interface";
import { GalleryAPI } from "../../api/admin/gallery";
import SearchRunnerCompetition from "./component/SearchRunnerCompetition";

const Gallery: React.FC = () => {
  const [list, setList] = useState<IGalleryResponse[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [mode, setMode] = useState("I");
  const [id, setId] = useState(0);
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [runnerCompetitionId, setRunnerCompetitionId] = useState<number>(0);
  const [galleryImages, setGalleryImages] = useState<IGalleryImagesResponse[]>(
    [],
  );
  const [isCompetition, setIsCompetition] = useState(false);

  const handleBasicFormAdd = async () => {
    await handleAdd();
  };

  const handleBasicFormSave = async () => {
    await handleSave();
  };

  const handleBasicFormRemove = async () => {
    await handleRemove();
  };

  const competitionFormData: FormProps = {
    title: "본사 대회 정보",
    form: [
      {
        value: title,
        setValue: setTitle,
        label: "타이틀",
        width: "100%",
        type: "input",
      },
      {
        value: files,
        setValue: setFiles,
        label: "이미지",
        width: "100%",
        type: "files",
        currentImageUrls: galleryImages.map((item) => item.imageUrl), // 이 부분 추가
      },
      {
        value: runnerCompetitionId,
        setValue: setRunnerCompetitionId,
        label: "연관 본사 대회 id",
        width: "100%",
        isOpen: isCompetition,
        setIsOpen: setIsCompetition,
        type: "search-group",
      },
    ],
    add: handleBasicFormAdd,
    save: handleBasicFormSave,
    remove: handleBasicFormRemove,
    mode: mode,
  };

  const headers = [
    {
      text: "ID",
      align: "center",
      sortable: false,
      value: "id",
    },
    {
      text: "타이틀",
      align: "center",
      sortable: false,
      value: "title",
    },
    {
      text: "생성일",
      align: "center",
      sortable: false,
      value: "createdAt",
    },
    {
      text: "연관 본사 대회 id",
      align: "center",
      sortable: false,
      value: "runnerCompetitionId",
    },
  ];

  const getList = async () => {
    try {
      const res = await GalleryAPI.getGalleryList();
      setList(res.data);
    } catch (error) {
      console.error("Failed to fetch competitions:", error);
    }
  };

  useEffect(() => {
    getList();
  }, []);

  const handleAdd = async () => {
    if (!title) {
      alert("타이틀을 입력해주세요.");
      return;
    }
    if (!files.length) {
      alert("이미지를 선택해주세요.");
      return;
    }

    const request = {
      title,
      runnerCompetitionId,
    };

    const formData = new FormData();

    if (files) {
      Array.from(files).forEach((file) => {
        formData.append("files", file);
      });
    }

    // JSON 데이터를 문자열로 변환하여 추가
    formData.append("data", JSON.stringify(request));

    if (window.confirm("신규등록 하시겠습니까?")) {
      try {
        const res = await GalleryAPI.registerGallery(formData);
        if (res?.status === 200 || res?.status === 201) {
          alert("신규 등록 되었습니다.");
          await getList();
          addForm();
        }
      } catch (error) {
        console.error("Registration failed:", error);

        // 에러 타입 가드 추가
        if (error instanceof AxiosError) {
          if (error.response) {
            console.error("Error details:", error.response.data);
            alert(error.response.data.message || "등록에 실패했습니다.");
          } else {
            alert("서버 연결에 실패했습니다.");
          }
        } else {
          alert("등록에 실패했습니다.");
        }
      }
    }
  };

  const addForm = () => {
    setMode("I");
    setId(0);
    setTitle("");
    setRunnerCompetitionId(0);
    setFiles([]);
    setGalleryImages([]);
  };

  const setDetail = async (item: IGalleryResponse) => {
    // 상세 정보 조회
    setMode("U");
    setId(item.id);
    setTitle(item.title);
    setRunnerCompetitionId(item.competitionDetailId);
    const images = await GalleryAPI.getGalleryImageList(item.id);
    setGalleryImages(images.data);
  };

  const handleSave = async () => {
    if (id > 0) {
      const request = {
        title,
        runnerCompetitionId,
      };
      const formData = new FormData();

      if (files) {
        Array.from(files).forEach((file) => {
          formData.append("files", file);
        });
      }

      // JSON 데이터를 문자열로 변환하여 추가
      formData.append("data", JSON.stringify(request));

      if (window.confirm("수정 하시겠습니까?")) {
        try {
          const res = await GalleryAPI.updateGallery(id, formData);
          if (res?.status === 200) {
            alert("수정되었습니다.");
            await getList();
            addForm();
          }
        } catch (error) {
          // ... 에러 처리
        }
      }
    }
  };

  const handleRemove = async () => {
    if (window.confirm("삭제 하시겠습니까?")) {
      if (id) {
        try {
          const res = await GalleryAPI.deleteGallery(id);
          if (res?.status === 200) {
            alert("삭제되었습니다.");
            await getList();
            addForm();
          }
        } catch (error) {
          alert("삭제에 실패했습니다.");
          console.error("Delete failed:", error);
        }
      }
    }
  };

  const handleSearch = (search: string) => {
    setCurrentPage(1);

    if (!search.trim()) {
      setList(list);
      const totalPages = Math.ceil(list.length / 10);
      setTotalPages(totalPages);
      return;
    }

    const filteredList = list.filter((item: IGalleryResponse) => {
      return Object.values(item).some((value) => {
        if (value) {
          return value.toString().toLowerCase().includes(search.toLowerCase());
        }
        return false;
      });
    });

    setList(filteredList);
    const totalPages = Math.ceil(filteredList.length / 10);
    setTotalPages(totalPages);
  };

  return (
    <DivStyle
      style={{ overflow: "auto" }}
      backgroundColor="#121212"
      height="100vh"
      display="flex"
      flex_direction="row"
      marginTop="65px"
      setMobile={true}
    >
      {isCompetition && (
        <SearchRunnerCompetition
          setRunnerCompetitionId={setRunnerCompetitionId}
          setIsOpen={setIsCompetition}
        />
      )}
      {/* 왼쪽 영역: 대회 목록 */}
      <DivStyle
        width="66%"
        height="auto"
        display="flex"
        flex_direction="column"
        align_items="center"
        setMobileTable={true}
      >
        <h2 style={{ color: "white", width: "auto", marginTop: "40px" }}>
          본사 대회 관리
        </h2>
        <DivStyle
          width="95%"
          display="flex"
          backgroundColor="#1E1E1E"
          flex_direction="column"
          borderRadius="10px"
          marginTop="30px"
          marginBottom="10px"
        >
          <DivStyle
            width="100%"
            display="flex"
            flex_direction="row"
            justify_content="end"
            align_items="center"
          >
            <DivStyle
              width="50%"
              display="flex"
              justify_content="flex-end"
              flex_direction="row"
              align_items="center"
              marginRight="20px"
              marginTop="20px"
            >
              <InputFormStyle onChange={(e) => handleSearch(e.target.value)} />
              <Icon path={mdiMagnify} size={1} style={{ color: "white" }} />
            </DivStyle>
          </DivStyle>
          <Table
            headers={headers}
            currentPage={currentPage}
            dataList={list}
            setDetail={setDetail}
          />
          <Page
            currentPage={currentPage}
            totalPages={totalPages}
            dataList={list}
            setCurrentPage={setCurrentPage}
          />
        </DivStyle>
        <DivStyle
          marginBottom="50px"
          width="95%"
          display="flex"
          justify_content="flex-end"
        >
          <ButtonStyle
            border="none"
            backgroundColor="#1976D2"
            width="85px"
            height="40px"
            color="white"
            borderRadius="5px"
            onClick={addForm}
          >
            신규 등록
          </ButtonStyle>
        </DivStyle>
      </DivStyle>

      {/* 오른쪽 영역: 입력 폼 */}
      <DivStyle
        width="33%"
        height="auto"
        display="flex"
        flex_direction="column"
        align_items="center"
        setMobileForm={true}
      >
        <h2 style={{ color: "white", width: "auto", marginTop: "40px" }}>
          대회 정보
        </h2>

        {/* 기본 정보 폼 */}
        <DivStyle
          width="95%"
          display="flex"
          backgroundColor="#1E1E1E"
          flex_direction="column"
          borderRadius="10px"
          marginTop="30px"
          marginBottom="30px"
        >
          <SmallForm {...competitionFormData} />
        </DivStyle>
        {galleryImages.length > 0 && (
          <PosterManager
            posters={galleryImages}
            setPosters={setGalleryImages}
            getData={getList}
          />
        )}
      </DivStyle>
    </DivStyle>
  );
};

export default Gallery;
